import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  load as loadRubric,
  loadTeamScores,
  loadTeamRubric,
  updateTeamRubric,
} from 'js/actions/rubric-actions';
import { load as loadTeam } from 'js/actions/team-actions';
import { useQuery } from 'js/hooks/useQuery';
import { QueryBase } from 'js/store/reducers/queries';
import { denormalize } from 'normalizr';
import { SCHEMAS } from 'js/api';
import { applyWeightSchemeToEvaluation } from 'js/utils/rubric';
import TeamRubricComponent from '../components/TeamRubricComponent';

const RUBRIC_TEAM_SCORES_IDLE_QUERY = `${QueryBase.RubricTeamScores}/__idle`;
const TEAM_RUBRIC_IDLE_QUERY = 'team-rubric/__idle';

function rubricTeamScoresQueryId(workspaceId, rubricId, teamId) {
  return `${QueryBase.RubricTeamScores}/${workspaceId}/${rubricId}/${teamId}`;
}

function teamRubricQueryId(workspaceId, rubricId, teamId) {
  return `team-rubric/${workspaceId}/${rubricId}/${teamId}`;
}

function TeamRubricContainer({
  team = null,
  rubric = null,
  loadTeam = () => {},
  loadRubric = () => {},
  loadTeamScores = () => Promise.resolve(null),
  loadTeamRubric = () => Promise.resolve(null),
  updateTeamRubric = () => Promise.resolve(null),
}) {
  const { workspaceId, teamId, rubricId } = useParams();
  /** `undefined` = follow server payload; otherwise local optimistic selection. */
  const [weightSchemeSelection, setWeightSchemeSelection] = useState(undefined);

  const queryId = useMemo(() => {
    if (!workspaceId || !rubricId || !teamId) {
      return RUBRIC_TEAM_SCORES_IDLE_QUERY;
    }
    return rubricTeamScoresQueryId(workspaceId, rubricId, teamId);
  }, [workspaceId, rubricId, teamId]);

  const teamRubricQueryIdMemo = useMemo(() => {
    if (!workspaceId || !rubricId || !teamId) {
      return TEAM_RUBRIC_IDLE_QUERY;
    }
    return teamRubricQueryId(workspaceId, rubricId, teamId);
  }, [workspaceId, rubricId, teamId]);

  const queryError = useSelector((state) => state.queries[queryId]?.error);

  const loadQuery = useCallback(
    (qid) => {
      if (qid === RUBRIC_TEAM_SCORES_IDLE_QUERY) {
        return Promise.resolve(null);
      }
      return loadTeamScores(rubricId, teamId, qid, {
        omitUnweighted: false,
      });
    },
    [loadTeamScores, rubricId, teamId]
  );

  const loadTeamRubricQuery = useCallback(
    (qid) => {
      if (qid === TEAM_RUBRIC_IDLE_QUERY) {
        return Promise.resolve(null);
      }
      return loadTeamRubric(teamId, rubricId, qid);
    },
    [loadTeamRubric, teamId, rubricId]
  );

  const {
    refresh,
    result: scoresData,
    isLoading,
    hasLoaded,
  } = useQuery({
    queryId,
    query: loadQuery,
  });

  const { refresh: refreshTeamRubric, result: teamRubricPayload } = useQuery({
    queryId: teamRubricQueryIdMemo,
    query: loadTeamRubricQuery,
  });

  useEffect(() => {
    if (queryId !== RUBRIC_TEAM_SCORES_IDLE_QUERY) {
      refresh();
    }
  }, [queryId, refresh]);

  useEffect(() => {
    if (teamRubricQueryIdMemo !== TEAM_RUBRIC_IDLE_QUERY) {
      refreshTeamRubric();
    }
  }, [teamRubricQueryIdMemo, refreshTeamRubric]);

  useEffect(() => {
    setWeightSchemeSelection(undefined);
  }, [teamRubricQueryIdMemo]);

  useEffect(() => {
    if (teamId) {
      loadTeam(teamId);
    }
  }, [loadTeam, teamId]);

  useEffect(() => {
    if (rubricId) {
      loadRubric(rubricId);
    }
  }, [loadRubric, rubricId]);

  const serverWeightScheme = teamRubricPayload?.weight_scheme ?? null;
  const effectiveWeightScheme =
    weightSchemeSelection !== undefined
      ? weightSchemeSelection
      : serverWeightScheme;

  const displayedScoresData = useMemo(() => {
    if (!scoresData) return scoresData;
    return applyWeightSchemeToEvaluation(
      scoresData,
      rubric?.document,
      String(effectiveWeightScheme ?? '').trim(),
      { defaultWeightsFromDocument: true }
    );
  }, [scoresData, rubric?.document, effectiveWeightScheme]);

  const handleWeightSchemeChange = useCallback(
    async (rawValue) => {
      const nextScheme = String(rawValue ?? '').trim() === '' ? null : rawValue;
      setWeightSchemeSelection(nextScheme);
      try {
        await updateTeamRubric(teamId, rubricId, {
          weight_scheme: nextScheme,
        });
        void refreshTeamRubric();
      } catch (_e) {
        setWeightSchemeSelection(undefined);
      }
    },
    [updateTeamRubric, refreshTeamRubric, rubricId, teamId]
  );

  const loadError = Boolean(queryError);
  const loading =
    queryId !== RUBRIC_TEAM_SCORES_IDLE_QUERY &&
    !loadError &&
    (isLoading || !hasLoaded);

  return (
    <TeamRubricComponent
      team={team}
      teamConnectorConfigurations={[]}
      teamConnectorConfigurationsHasLoaded={false}
      rubric={rubric}
      scoresData={displayedScoresData}
      loading={loading}
      loadError={loadError}
      weightScheme={effectiveWeightScheme}
      onWeightSchemeChange={handleWeightSchemeChange}
    />
  );
}

export default connect(
  (state, ownProps) => {
    const { match } = ownProps;
    const { teamId, rubricId } = match.params;
    const { entities } = state;

    return {
      team: denormalize(entities.teams[teamId], SCHEMAS.TEAM, entities),
      rubric: denormalize(entities.rubrics[rubricId], SCHEMAS.RUBRIC, entities),
    };
  },
  {
    loadTeam,
    loadRubric,
    loadTeamScores,
    loadTeamRubric,
    updateTeamRubric,
  }
)(TeamRubricContainer);
